import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于久久综合',
  description: '久久综合是专注多元内容聚合的一站式平台，汇集影视、音乐、图文、资讯等丰富资源。',
  about: {
    '@type': 'Organization',
    name: '久久综合',
    description: '久久综合 - 海量资源一站式聚合平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@jiujiuzonghe.com',
      contactType: 'customer service',
    },
  },
};

const highlights = [
  {
    icon: 'ri-stack-line',
    title: '海量资源',
    desc: '汇聚六大分类超过12,000条优质内容资源，涵盖影视、音乐、图文、资讯、学习和工具',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    desc: '编辑团队全天候关注各领域动态，确保平台内容每日更新，让您始终获取最新信息',
  },
  {
    icon: 'ri-search-line',
    title: '便捷搜索',
    desc: '强大的全文搜索功能，支持关键词模糊匹配，帮助您快速找到感兴趣的内容资源',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: '清晰分类',
    desc: '六大内容分类一目了然，每个分类下设有精细化的标签体系，便于内容检索和浏览',
  },
];

const stats = [
  { num: '12,000+', label: '内容资源' },
  { num: '6', label: '核心分类' },
  { num: '50万+', label: '月活跃用户' },
  { num: '99.9%', label: '服务可用率' },
  { num: '365', label: '全年无休更新' },
];

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <SEOHead
        title="关于我们 - 久久综合"
        description="久久综合是专注多元内容聚合的一站式平台，汇集影视、音乐、图文、资讯等丰富资源。了解久久综合的品牌故事与发展历程。"
        keywords="久久综合,关于久久综合,久久综合平台介绍,久久综合品牌"
        canonicalUrl="https://jiujiuzonghe.com/about"
      />
      <main className="min-h-screen pt-20 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <section className="text-center mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于久久综合
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              久久综合是专注多元内容聚合的一站式平台，致力于为用户提供极致的浏览体验。我们汇集了影视、音乐、图文、资讯等丰富资源，让您在一个平台上满足全方位内容需求。
            </p>
          </section>

          <section className="mb-14">
            <div className="relative w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20creative%20workspace%20with%20content%20creation%20team%20collaborating%20warm%20natural%20light%20wooden%20desk%20large%20monitor%20screens%20artistic%20atmosphere%20editorial%20photography%20clean%20composition&width=1600&height=700&seq=about-banner-jiujiu&orientation=landscape"
                alt="久久综合团队"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-4 bg-background-50 rounded-xl border border-background-200/70"
                >
                  <div className="text-xl md:text-2xl font-bold text-primary-600 font-heading">{stat.num}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
              品牌优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {highlights.map((item, i) => (
                <div
                  key={i}
                  className="p-6 bg-background-50 rounded-xl border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-xl">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
              品牌口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  quote: '久久综合是我每天必看的平台，内容更新快，分类清晰，搜索也很方便。从影视资讯到学习资料，应有尽有。',
                  author: '张先生',
                  role: '资深用户',
                },
                {
                  quote: '作为内容创作者，久久综合给了我很多灵感。平台上的内容质量很高，编辑推荐也很专业，是我获取行业资讯的首选。',
                  author: '李女士',
                  role: '内容创作者',
                },
                {
                  quote: '久久综合的界面设计简洁大方，浏览体验非常好。特别是在手机上也能流畅阅读，通勤路上必备的应用。',
                  author: '王同学',
                  role: '大学生用户',
                },
              ].map((testimonial, i) => (
                <div
                  key={i}
                  className="p-6 bg-background-50 rounded-xl border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{testimonial.author}</p>
                      <p className="text-xs text-foreground-400">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
              联系我们
            </h2>
            <div className="max-w-2xl mx-auto bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</p>
                    <a href="mailto:support@jiujiuzonghe.com" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                      support@jiujiuzonghe.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">客服热线</p>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                    <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00-18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">公司地址</p>
                    <p className="text-sm text-foreground-500">北京市海淀区中关村科技园区</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">微信公众号</p>
                    <p className="text-sm text-foreground-500">久久综合平台</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}